import get from 'lodash/get';
import { ACTION_NAMES, APP_LOG_TYPE, tracking } from 'services/Tracking';
import {
    SUBSCRIBED_OR_SUSPENDED,
    THREE_D_SECURE_ERROR_CODE,
    SUBSCRIPTION_ALREADY_IN_USE,
} from 'aa/vue/constants/shared';
import { scrollTo } from 'helpers/scrollTo';
import {
    SHOW_PICKER,
    MFE_ENABLED,
    MFE_USERPROFILES_ENABLED,
    FTC_COMPLIANCE_ENABLED,
} from 'helpers/featureConstants';
import { setCookie } from 'helpers/VanillaCookie';
import { FLOW_TYPE } from 'models/FlowModel';
import { NFL_BANNER_FLAG } from 'helpers/sharedConstants';
import { SHOW_PICKER_PATH } from 'aa/vue/constants/aaRoutes';
import isEmpty from 'lodash/isEmpty';

export function displayFeedbackToUser(message) {
    this.formErrorMessage = message;
}

export function handleProcessedPayment(resp) {
    const couponCode = get(this, 'coupon.recurlyInfo.code');

    const trackingParams = {
        ...resp.tr,
        purchasePromoCode: couponCode,
        userRegType: this.$store.getters['flow/isRendezvous']
            ? 'rendezvous'
            : this.$store.getters['flow/userRegType'],
        pageType: 'svod_complete',
        pickPlanType: this.plan?.planType,
        pickPlanSku: this.plan?.recurlyCode,
    };

    if (this.$store.getters.featureIsActive(FTC_COMPLIANCE_ENABLED)) {
        // This is the same as this.showCheckbox in SummaryFTC, but we don't have it in the context here
        const isFtcCheckboxShown = this.$store.getters['ftc/getNeedsCheckbox'];

        trackingParams.pickPlanType = this.plan.titleForTracking;

        trackingParams.ftcCheckboxShown = isFtcCheckboxShown ? '1' : '0';

        trackingParams.ftcCheckboxSelect =
            this.$store.getters['ftc/getCoolingRightsWaived'] && isFtcCheckboxShown ? '1' : '0';
    }

    if (
        this.$store.getters['flow/isRendezvous'] &&
        this.$root.serverData.enhancedTrackActivationSuccess
    ) {
        trackingParams.activationDeviceFamily =
            this.$root.serverData.flow?.activationDeviceFamily || '';
    }
    tracking.trackPayment(trackingParams);
    CBS.CombinedCookie.set('graph', 'couponCode', couponCode || null);

    this.submitStatus = this.$getLocale('the_account_has_been_created_successfully');

    // set cookie & localStorage to show nfl game pass banner on home page and hide for showtime bundle
    if (
        this.$store.getters['flow/type'] === FLOW_TYPE.DEFAULT &&
        !this.$store.getters['bundleShowtime']
    ) {
        setCookie(NFL_BANNER_FLAG, '1', { path: '/' });
        window.localStorage.setItem(NFL_BANNER_FLAG, '0');
    }

    // Add short delay to allow tracking call to complete
    setTimeout(() => {
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('promoOffer');
        this.$store.dispatch('plan/clearPlanInfoInSessionStorage');
        if (this.$store.getters['flow/isRendezvous']) {
            window.location.href = this.$store.getters['flow/destinationUrl'];
        } else {
            const showMFEUserProfile =
                this.$store.state.featureFlags[MFE_ENABLED] &&
                this.$store.state.featureFlags[MFE_USERPROFILES_ENABLED];

            if (this.$store.state.featureFlags[SHOW_PICKER]) {
                let nextStep = this.$store.getters['flow/getNextStep'];
                if (nextStep) {
                    if (nextStep.name === 'ShowsPicker' && showMFEUserProfile) {
                        window.location.href = SHOW_PICKER_PATH;
                        return;
                    } else {
                        this.$router.push({ name: nextStep.name });
                        return;
                    }
                }
            }
            window.location.href = '/';
        }
    }, 500);
}

export function handleProcessedPaymentError(err, trackingParams = {}) {
    const code = get(err, 'data.code') || err?.code || 'No code provided';
    const errorMessage = err.message || 'No message provided';
    const actionTokenId = get(err, 'data.actionTokenId') || err?.actionTokenId;
    this.formIsProcessing = false;

    if (code === THREE_D_SECURE_ERROR_CODE) {
        this.setUpThreeDSecure(actionTokenId);
    } else if (code === SUBSCRIPTION_ALREADY_IN_USE) {
        tracking.trackAction(ACTION_NAMES.APP_LOG, {
            appLogText: errorMessage,
            appLogType: SUBSCRIPTION_ALREADY_IN_USE,
            appLogCode: code,
            ...trackingParams,
        });
    } else if (err.message || err.data.code) {
        tracking.trackAction(ACTION_NAMES.APP_LOG, {
            appLogText: errorMessage,
            appLogType: APP_LOG_TYPE.PURCHASE_ERROR,
            appLogCode: code,
            ...trackingParams,
        });

        if (errorMessage === SUBSCRIBED_OR_SUSPENDED) {
            window.location.href = '/';
        } else {
            this.formErrorMessage =
                err.data && err.data.code ? this.$getLocale(err.data.code) : err.message;

            this.formErrorCode = err?.data?.errorCode;
            if (this.errorMessageElementId) {
                const el = document.getElementById(this.errorMessageElementId);
                scrollTo.element(el);
            } else {
                scrollTo.top();
            }
        }
    }
}

/**
 *  This function will check if we've fetched the user's billing info
 *  otherwise will send a request to fetch
 */
export async function fetchStoredPaymentIfEmpty(store) {
    if (!store) {
        console.error('No store found');
        return;
    }

    if (store.getters['storedPaymentMethod/alreadyCheckedForStoredPaymentMethod']) {
        return;
    } else {
        if (!isEmpty(store.getters.getServerDataByKey('billingInfo'))) {
            store.commit(
                'storedPaymentMethod/setBillingInfo',
                store.getters.getServerDataByKey('billingInfo'),
            );
        } else {
            await store.dispatch('storedPaymentMethod/updateBillingInfo');
        }
    }
}

/**
 *  This function is used to check if the user has a valid stored payment method
 *  returns @boolean
 */
export async function hasValidStoredPaymentMethod(store) {
    if (!store) {
        console.error('No store found');
        return false;
    }

    if (store.getters['storedPaymentMethod/alreadyCheckedForStoredPaymentMethod']) {
        return store.getters['storedPaymentMethod/hasValidStoredPaymentMethod'];
    } else {
        /* We may need to fetch it via an XHR call  */
        //first check if it is already in the serverData
        if (!isEmpty(store.getters.getServerDataByKey('billingInfo'))) {
            //if it is in the serverData, we can use it
            store.commit(
                'storedPaymentMethod/setBillingInfo',
                store.getters.getServerDataByKey('billingInfo'),
            );
            return store.getters['storedPaymentMethod/hasValidStoredPaymentMethod'];
        } else {
            // this will never get called because billingInfo is set in the constructor of the store, but leaving it here for future use just in case the constructor is changed
            try {
                await store.dispatch('storedPaymentMethod/updateBillingInfo');
            } catch (error) {
                return false;
            }
            return store.getters['storedPaymentMethod/hasValidStoredPaymentMethod'];
        }
    }
}

/**
 * This function is used to check if the path should redirect to the summary page
 * to make use of their valid stored payment method
 * @param {*} store
 * @returns boolean || string
 */
export async function redirectLinkToSummary(store) {
    // Check if the feature is enabled or if they are an ex-subscriber
    const isExSubscriber = store.getters['user/isExSubscriber'];
    const paymentExsubBillingSummary = store.getters.getServerDataByKey(
        'paymentExsubBillingSummary',
    );
    const ftcComplianceEnabled = store.getters.featureIsActive(FTC_COMPLIANCE_ENABLED) ?? false;
    if (!isExSubscriber || !paymentExsubBillingSummary || ftcComplianceEnabled) {
        return false;
    }

    /* 
    Now that we know this feature is enabled we need to check if the user has a valid stored payment method
    */
    const regionBaseUrl = store.getters.getServerDataByKey('regionBaseUrl');
    const pathForStoredPaymentMethodSummary = `${regionBaseUrl}/account/signup/summary/`;

    let redirect = false;
    const processedHasValidStoredPaymentMethod = await hasValidStoredPaymentMethod(store);
    if (processedHasValidStoredPaymentMethod) {
        redirect = pathForStoredPaymentMethodSummary;
    }
    return redirect;
}

/**
 * This function is used to check if the path should redirect to the normal payment page
 * since their stored payment method is not valid
 * @param {*} store
 * @returns boolean || string
 */
export async function redirectLinkToSubmitPayment(store) {
    let shouldRedirect = false;
    const summaryPageAvailableForUser = await redirectLinkToSummary(store);
    if (!summaryPageAvailableForUser) {
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        const regionBaseUrl = store.getters.getServerDataByKey('regionBaseUrl');
        const pathForSubmitPayment = `${regionBaseUrl}/account/signup/submitpayment/`;
        shouldRedirect = pathForSubmitPayment;
    }

    return shouldRedirect;
}
